/**
 * V2 几何工具 - 顶点数 / 面积 / 自相交校验 + bbox / 格式化, 全部基于 turf。
 */

// 按需引入具体 turf 包, 不用 '@turf/turf' 桶文件:
// 桶文件会拖入 clusters-dbscan → geokdbush (纯 ESM), 既让 jest 无法转换, 也白白增大 bundle。
import turfArea from '@turf/area';
import turfBbox from '@turf/bbox';
import turfCentroid from '@turf/centroid';
import turfKinks from '@turf/kinks';
import {
    AREA_LIMIT_LEVEL_1_SQM,
    AREA_LIMIT_LEVEL_2_SQM,
    MIN_VERTICES,
} from '../constants';
import { GeoJsonGeometry } from '../types';

/**
 * 数多边形外环顶点数 (不含闭合重复点)。MultiPolygon 取各 polygon 外环之和。
 */
export function countVertices(geometry: GeoJsonGeometry | null | undefined): number {
    if (!geometry) return 0;
    if (geometry.type === 'Polygon') {
        const rings = geometry.coordinates as number[][][];
        return ringVertexCount(rings?.[0]);
    }
    if (geometry.type === 'MultiPolygon') {
        const polys = geometry.coordinates as number[][][][];
        return (polys ?? []).reduce((sum, poly) => sum + ringVertexCount(poly?.[0]), 0);
    }
    return 0;
}

function ringVertexCount(ring: number[][] | undefined): number {
    if (!ring || ring.length === 0) return 0;
    const first = ring[0];
    const last = ring[ring.length - 1];
    const closed = first && last && first[0] === last[0] && first[1] === last[1];
    return closed ? ring.length - 1 : ring.length;
}

/** 面积 (m²), 解析失败返回 0。 */
export function areaSqm(geometry: GeoJsonGeometry | null | undefined): number {
    if (!geometry) return 0;
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return turfArea({ type: 'Feature', properties: {}, geometry } as any);
    } catch {
        return 0;
    }
}

/** 面积展示: >= 0.01km² 显示 km², 否则 m²。 */
export function formatArea(sqm: number): string {
    if (sqm >= 10_000) return `${(sqm / 1_000_000).toFixed(3)} km²`;
    return `${Math.round(sqm)} m²`;
}

/** 几何重心 (中心点近似, 前端展示用)。 */
export function centroidOf(geometry: GeoJsonGeometry | null | undefined): { lng: number; lat: number } | null {
    if (!geometry) return null;
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const c = turfCentroid({ type: 'Feature', properties: {}, geometry } as any);
        const [lng, lat] = c.geometry.coordinates;
        return { lng, lat };
    } catch {
        return null;
    }
}

/** [minLng, minLat, maxLng, maxLat]; 解析失败返回 null。 */
/** 经纬度 bbox: [minLng, minLat, maxLng, maxLat]。 */
export type TileBbox = [number, number, number, number];

/**
 * Web Mercator 瓦片 (z/x/y) 的经纬度范围是否与 bbox 相交。
 * 用于瓦片按范围失效: 编辑面的 bbox 压到哪些片, 哪些片才换版本冷拉。
 */
export function tileIntersectsBbox(z: number, x: number, y: number, bbox: TileBbox): boolean {
    const n = 2 ** z;
    const lonW = (x / n) * 360 - 180;
    const lonE = ((x + 1) / n) * 360 - 180;
    const latN = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;
    const latS = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180) / Math.PI;
    return bbox[0] <= lonE && bbox[2] >= lonW && bbox[1] <= latN && bbox[3] >= latS;
}

export function bboxOf(geometry: GeoJsonGeometry | null | undefined): [number, number, number, number] | null {
    if (!geometry) return null;
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const b = turfBbox({ type: 'Feature', properties: {}, geometry } as any);
        return [b[0], b[1], b[2], b[3]];
    } catch {
        return null;
    }
}

/** 多个几何的合并 bbox。 */
export function bboxOfAll(geometries: (GeoJsonGeometry | null | undefined)[]): [number, number, number, number] | null {
    let out: [number, number, number, number] | null = null;
    for (const g of geometries) {
        const b = bboxOf(g);
        if (!b) continue;
        if (!out) {
            out = [...b] as [number, number, number, number];
        } else {
            out[0] = Math.min(out[0], b[0]);
            out[1] = Math.min(out[1], b[1]);
            out[2] = Math.max(out[2], b[2]);
            out[3] = Math.max(out[3], b[3]);
        }
    }
    return out;
}

/**
 * 去掉每个环里与前一个点完全相同的连续重复顶点 (零长边)。
 *
 * <p>存量数据 (导入源) 偶有首点写两遍等重复点, turf.kinks 会把零长边误判为自相交,
 * 而后端 PostGIS ST_IsValid 视其为合法。只比前一个点、不做首尾环绕比较, 首点永不删,
 * 故闭合环去重后仍首尾相等。只删严格相等的点, 不合并近似点、不删共线点 (不改用户画的形状)。
 * 无重复点时原样返回入参。
 */
export function removeConsecutiveDuplicates<T extends GeoJsonGeometry | null | undefined>(geometry: T): T {
    if (!geometry) return geometry;
    let changed = false;
    const dedupeRing = (ring: number[][]): number[][] => {
        const out = ring.filter((p, i) => i === 0 || p[0] !== ring[i - 1][0] || p[1] !== ring[i - 1][1]);
        if (out.length !== ring.length) changed = true;
        return out;
    };
    if (geometry.type === 'Polygon') {
        const rings = (geometry.coordinates as number[][][]).map(dedupeRing);
        return changed ? ({ ...geometry, coordinates: rings } as T) : geometry;
    }
    if (geometry.type === 'MultiPolygon') {
        const polys = (geometry.coordinates as number[][][][]).map((poly) => poly.map(dedupeRing));
        return changed ? ({ ...geometry, coordinates: polys } as T) : geometry;
    }
    return geometry;
}

/** 校验结果。 */
export interface GeometryValidation {
    ok: boolean;
    message?: string;
}

/**
 * 保存前的前端几何校验: 自相交 (turf.kinks) / 顶点 ≥ 3 / 面积按级别上限。
 * 顶点上限不在前端卡 (后端动态配置, 见 constants.ts), 由后端 VERTEX_LIMIT 拦。
 *
 * @param geometry   GeoJSON Polygon / MultiPolygon
 * @param levelEnum  LEVEL_1 | LEVEL_2 (null = 不校验面积上限, 只查自相交 + 顶点)
 */
export function validateAoiGeometry(rawGeometry: GeoJsonGeometry | null | undefined, levelEnum: string | null): GeometryValidation {
    if (!rawGeometry) return { ok: false, message: '几何为空, 请先绘制 AOI 面' };
    // 先去零长边, 否则 kinks 误报自相交; 提交侧同样去重 (AoiDrawer), 校验与提交口径一致
    const geometry = removeConsecutiveDuplicates(rawGeometry);

    const vertices = countVertices(geometry);
    if (vertices < MIN_VERTICES) {
        return { ok: false, message: `顶点数不足: 至少 ${MIN_VERTICES} 个 (当前 ${vertices})` };
    }

    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const kinks = turfKinks({ type: 'Feature', properties: {}, geometry } as any);
        if (kinks.features.length > 0) {
            return { ok: false, message: '多边形自相交, 请调整顶点后重试' };
        }
    } catch {
        return { ok: false, message: '几何格式错误, 请重新绘制' };
    }

    if (levelEnum) {
        const limit = levelEnum === 'LEVEL_1' ? AREA_LIMIT_LEVEL_1_SQM : AREA_LIMIT_LEVEL_2_SQM;
        const area = areaSqm(geometry);
        if (area > limit) {
            return {
                ok: false,
                message: `面积 ${formatArea(area)} 超出${levelEnum === 'LEVEL_1' ? '1级 2 km²' : '2级 15 km²'}上限`,
            };
        }
    }
    return { ok: true };
}

/** 安全解析 GeoJSON 字符串。 */
export function parseGeometry(geoJson: string | null | undefined): GeoJsonGeometry | null {
    if (!geoJson) return null;
    try {
        const obj = JSON.parse(geoJson);
        if (obj && typeof obj === 'object' && typeof obj.type === 'string') return obj as GeoJsonGeometry;
        return null;
    } catch {
        return null;
    }
}
